// The signed-in user's preferences, kept in the browser so pages can read them without waiting.
// The server is the source of truth: the copy here is refreshed whenever they are loaded or saved.

export const NOTIFICATION_CATEGORIES = ['inventory', 'sales', 'purchase', 'manufacturing', 'finance', 'system'] as const;
export type NotificationCategoryKey = (typeof NOTIFICATION_CATEGORIES)[number];

export interface UserPreferences {
  // The page opened after signing in
  startPage: string;
  // What lists start with
  rowsPerPage: number;
  notifications: {
    // Show a pop-up when a notification arrives
    popups: boolean;
    // false switches the alerts of that area off
    categories: Record<NotificationCategoryKey, boolean>;
  };
}

export const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

export const DEFAULT_PREFERENCES: UserPreferences = {
  startPage: '/dashboard',
  rowsPerPage: 10,
  notifications: {
    popups: true,
    categories: { inventory: true, sales: true, purchase: true, manufacturing: true, finance: true, system: true },
  },
};

const STORAGE_KEY = 'preferences';

export const getCachedPreferences = (): UserPreferences => {
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    if (!stored || typeof stored !== 'object') return DEFAULT_PREFERENCES;
    return {
      ...DEFAULT_PREFERENCES,
      ...stored,
      notifications: {
        ...DEFAULT_PREFERENCES.notifications,
        ...(stored.notifications ?? {}),
        categories: { ...DEFAULT_PREFERENCES.notifications.categories, ...(stored.notifications?.categories ?? {}) },
      },
    };
  } catch {
    return DEFAULT_PREFERENCES;
  }
};

export const cachePreferences = (preferences: UserPreferences) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
  } catch {
    // Without storage the defaults are used
  }
};

export const clearCachedPreferences = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to clear
  }
};

// The number of rows a list starts with; pass it to useState as the initial value
export const defaultRowsPerPage = (): number => {
  const rows = getCachedPreferences().rowsPerPage;
  return ROWS_PER_PAGE_OPTIONS.includes(rows) ? rows : DEFAULT_PREFERENCES.rowsPerPage;
};

// Where to go after signing in
export const startPage = (): string => getCachedPreferences().startPage || DEFAULT_PREFERENCES.startPage;
